<script setup lang="ts">
import { ref, watch, nextTick, onMounted, onUnmounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useVirtualizer } from '@tanstack/vue-virtual';
import { parseAnsi } from '../utils/ansi';
import { parseStages, hasStages, type Stage, type StageStatus } from '../utils/stageParser';
import { extractSelectedLogText, hasTextSelection } from '../utils/logSelection';

interface LogLine {
  timestamp: string;
  stream: 'stdout' | 'stderr';
  line: string;
}

type StreamingState = 'disconnected' | 'connecting' | 'replaying_history' | 'streaming' | 'finished';

// Virtual list item types - originalIndex is the 0-based index in the original logs array
type VirtualItem =
  | { type: 'pre-stage-log'; log: LogLine; index: number; originalIndex: number }
  | { type: 'stage-header'; stage: Stage; stageIndex: number }
  | { type: 'stage-log'; log: LogLine; stage: Stage; stageIndex: number; logIndex: number; originalIndex: number }
  | { type: 'flat-log'; log: LogLine; index: number; originalIndex: number };

const props = defineProps<{
  logs: LogLine[];
  connected: boolean;
  streamingState?: StreamingState;
  buildStatus?: string;
}>();

const emit = defineEmits<{
  connect: [];
  disconnect: [];
  clear: [];
}>();

const route = useRoute();

const autoScroll = ref(true);
const fullscreen = ref(false);
const logContainer = ref<HTMLDivElement | null>(null);
const collapsedStages = ref<Set<string>>(new Set());
const selectedLine = ref<number | null>(null); // 1-based line number from URL hash

// Parse logs into stages
const stageGroup = computed(() => parseStages(props.logs));
const showStages = computed(() => hasStages(props.logs));

// Compute effective streaming state
const effectiveState = () => props.streamingState || (props.connected ? 'streaming' : 'disconnected');

// Build flattened virtual items list
const virtualItems = computed<VirtualItem[]>(() => {
  if (props.logs.length === 0) return [];

  if (showStages.value) {
    const items: VirtualItem[] = [];

    // Pre-stage logs (originalIndex = i since these come first)
    for (let i = 0; i < stageGroup.value.preStage.length; i++) {
      items.push({ type: 'pre-stage-log', log: stageGroup.value.preStage[i], index: i, originalIndex: i });
    }

    // Stage sections
    for (let stageIndex = 0; stageIndex < stageGroup.value.stages.length; stageIndex++) {
      const stage = stageGroup.value.stages[stageIndex];

      // Stage header (always visible)
      items.push({ type: 'stage-header', stage, stageIndex });

      // Stage logs (only if not collapsed)
      if (!collapsedStages.value.has(stage.name)) {
        for (let logIndex = 0; logIndex < stage.logs.length; logIndex++) {
          const log = stage.logs[logIndex];
          // Skip stage markers
          if (!isStageMarker(log.line)) {
            items.push({ type: 'stage-log', log, stage, stageIndex, logIndex, originalIndex: stage.startIndex + logIndex });
          }
        }
      }
    }

    return items;
  } else {
    // Flat view
    return props.logs.map((log, index) => ({
      type: 'flat-log' as const,
      log,
      index,
      originalIndex: index,
    }));
  }
});

// Virtual list configuration
const ROW_HEIGHT = 24; // Approximate height of each row
const STAGE_HEADER_HEIGHT = 48;

const getItemHeight = (index: number): number => {
  const item = virtualItems.value[index];
  if (!item) return ROW_HEIGHT;
  return item.type === 'stage-header' ? STAGE_HEADER_HEIGHT : ROW_HEIGHT;
};

const virtualizer = useVirtualizer({
  get count() {
    return virtualItems.value.length;
  },
  getScrollElement: () => logContainer.value,
  estimateSize: (index: number) => getItemHeight(index),
  overscan: 20,
});

// Auto-scroll when new logs arrive
watch(
  () => props.logs.length,
  async () => {
    if (autoScroll.value && logContainer.value) {
      await nextTick();
      virtualizer.value.scrollToIndex(virtualItems.value.length - 1, { align: 'end' });
    }
  }
);

// Re-measure when stages collapse/expand
watch(
  collapsedStages,
  () => {
    virtualizer.value.measure();
  },
  { deep: true }
);

function formatTime(timestamp: string): string {
  return new Date(timestamp).toISOString().substring(11, 23);
}

function handleScroll() {
  if (!logContainer.value) return;
  const { scrollTop, scrollHeight, clientHeight } = logContainer.value;
  autoScroll.value = scrollHeight - scrollTop - clientHeight < 100;
}

function toggleFullscreen() {
  // Capture first visible line before toggling
  const items = virtualizer.value.getVirtualItems();
  const firstVisibleIndex = items.length > 0 ? items[0].index : 0;

  fullscreen.value = !fullscreen.value;

  // After Teleport moves the DOM, restore scroll position
  nextTick(() => {
    virtualizer.value.scrollToIndex(firstVisibleIndex, { align: 'start' });
  });
}

function handleKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && fullscreen.value) {
    fullscreen.value = false;
  }
}

// Check if build is in a terminal state
function isBuildFinished(): boolean {
  return ['success', 'failed', 'cancelled'].includes(props.buildStatus || '');
}

function toggleStage(stageName: string) {
  const newSet = new Set(collapsedStages.value);
  if (newSet.has(stageName)) {
    newSet.delete(stageName);
  } else {
    newSet.add(stageName);
  }
  collapsedStages.value = newSet;
}

function isStageCollapsed(stageName: string): boolean {
  return collapsedStages.value.has(stageName);
}

function getStageStatusClass(status: StageStatus): string {
  return `stage-status-${status}`;
}

function getStageStatusIcon(stage: Stage): string {
  switch (stage.status) {
    case 'pending': return '\u25CB'; // Circle outline
    case 'running': return '\u25D4'; // Circle with right half filled
    case 'success': return '\u2713'; // Checkmark
    case 'failed': return '\u2717'; // X mark
    default: return '\u25CB';
  }
}

function isStageMarker(line: string): boolean {
  const trimmed = line.trim();
  return trimmed.startsWith('=== Stage');
}

function collapseAll() {
  const newSet = new Set<string>();
  stageGroup.value.stages.forEach(stage => {
    newSet.add(stage.name);
  });
  collapsedStages.value = newSet;
}

function expandAll() {
  collapsedStages.value = new Set();
}

function scrollToStage(stageName: string) {
  // Ensure stage is expanded
  const newSet = new Set(collapsedStages.value);
  newSet.delete(stageName);
  collapsedStages.value = newSet;

  // Find the index of the stage header in virtual items
  nextTick(() => {
    const index = virtualItems.value.findIndex(
      item => item.type === 'stage-header' && item.stage.name === stageName
    );
    if (index >= 0) {
      virtualizer.value.scrollToIndex(index, { align: 'start' });
    }
  });
}

// Helper functions for template type access
function getItemLog(index: number): LogLine {
  const item = virtualItems.value[index];
  if (item.type === 'pre-stage-log' || item.type === 'stage-log' || item.type === 'flat-log') {
    return item.log;
  }
  // Should never happen if template guards are correct
  return { timestamp: '', stream: 'stdout', line: '' };
}

function getItemStage(index: number): Stage {
  const item = virtualItems.value[index];
  if (item.type === 'stage-header' || item.type === 'stage-log') {
    return item.stage;
  }
  // Should never happen if template guards are correct
  return { name: '', status: 'pending', logs: [], startIndex: 0 };
}

function getItemOriginalIndex(index: number): number | null {
  const item = virtualItems.value[index];
  if (item.type === 'pre-stage-log' || item.type === 'stage-log' || item.type === 'flat-log') {
    return item.originalIndex;
  }
  return null;
}

// Handle clicking on a log line to get shareable URL
async function handleLineClick(virtualIndex: number) {
  // A drag ending on the line is a text selection, not a request for a permalink
  if (hasTextSelection(window.getSelection())) return;

  const originalIndex = getItemOriginalIndex(virtualIndex);
  if (originalIndex === null) return;

  const lineNumber = originalIndex + 1; // 1-based for display
  selectedLine.value = lineNumber;

  // Update URL hash without triggering Vue Router navigation
  const newUrl = new URL(window.location.href);
  newUrl.hash = `L${lineNumber}`;
  window.history.replaceState(null, '', newUrl.toString());

  // Copy to clipboard
  try {
    await navigator.clipboard.writeText(newUrl.toString());
  } catch {
    // Silently fail if clipboard access denied
  }
}

// Copy only the log text, without line numbers, timestamps and stream labels
function handleCopy(e: ClipboardEvent) {
  if (!logContainer.value || !e.clipboardData) return;
  const text = extractSelectedLogText(window.getSelection(), logContainer.value);
  if (text === null) return;
  e.clipboardData.setData('text/plain', text);
  e.preventDefault();
}

// Parse line number from URL hash
function parseLineFromHash(): number | null {
  const hash = route.hash;
  if (!hash) return null;
  const match = hash.match(/^#L(\d+)$/);
  return match ? parseInt(match[1], 10) : null;
}

// Scroll to a specific line number (1-based)
function scrollToLine(lineNumber: number) {
  const targetIndex = lineNumber - 1; // Convert to 0-based

  // Find the virtual item with this original index
  const virtualIndex = virtualItems.value.findIndex(item => {
    if (item.type === 'pre-stage-log' || item.type === 'stage-log' || item.type === 'flat-log') {
      return item.originalIndex === targetIndex;
    }
    return false;
  });

  if (virtualIndex >= 0) {
    // If in a collapsed stage, expand it first
    const item = virtualItems.value[virtualIndex];
    if (item.type === 'stage-log' && collapsedStages.value.has(item.stage.name)) {
      const newSet = new Set(collapsedStages.value);
      newSet.delete(item.stage.name);
      collapsedStages.value = newSet;
    }

    selectedLine.value = lineNumber;
    autoScroll.value = false; // Disable auto-scroll when navigating to a line

    nextTick(() => {
      // Re-find index after potential stage expansion
      const newVirtualIndex = virtualItems.value.findIndex(item => {
        if (item.type === 'pre-stage-log' || item.type === 'stage-log' || item.type === 'flat-log') {
          return item.originalIndex === targetIndex;
        }
        return false;
      });
      if (newVirtualIndex >= 0) {
        virtualizer.value.scrollToIndex(newVirtualIndex, { align: 'center' });
      }
    });
  }
}

// Watch for hash changes
watch(() => route.hash, (newHash) => {
  if (newHash) {
    const lineNumber = parseLineFromHash();
    if (lineNumber && props.logs.length > 0) {
      scrollToLine(lineNumber);
    }
  } else {
    selectedLine.value = null;
  }
});

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);

  // Check for line number in URL hash on mount
  const lineNumber = parseLineFromHash();
  if (lineNumber) {
    // Wait for logs to load, then scroll
    const unwatch = watch(
      () => props.logs.length,
      (length) => {
        if (length > 0 && lineNumber <= length) {
          scrollToLine(lineNumber);
          unwatch();
        }
      },
      { immediate: true }
    );
  }
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});
</script>

<template>
  <Teleport to="body" :disabled="!fullscreen">
    <div class="log-viewer" :class="{ fullscreen }">
      <div v-if="fullscreen" class="fullscreen-overlay" @click="fullscreen = false"></div>
      <div class="log-viewer-inner" :class="{ fullscreen }">
        <div class="log-toolbar">
          <div class="toolbar-left">
            <!-- Status indicator based on streaming state -->
            <span v-if="effectiveState() === 'replaying_history'" class="status replaying">
              <span class="dot pulse"></span> Loading history...
            </span>
            <span v-else-if="effectiveState() === 'streaming'" class="status streaming">
              <span class="dot pulse"></span> Live
            </span>
            <span v-else-if="effectiveState() === 'finished' || (isBuildFinished() && logs.length > 0)" class="status finished">
              <span class="dot"></span> Complete
            </span>
            <span v-else-if="effectiveState() === 'connecting'" class="status connecting">
              <span class="dot pulse"></span> Connecting...
            </span>
            <span v-else-if="connected" class="status connected">
              <span class="dot"></span> Connected
            </span>
            <span v-else class="status disconnected">
              <span class="dot"></span> Disconnected
            </span>
            <span class="log-count">{{ logs.length }} lines</span>
            <template v-if="showStages">
              <span class="stage-count">{{ stageGroup.stages.length }} stages</span>
            </template>
          </div>
          <div class="toolbar-right">
            <template v-if="showStages">
              <button @click="expandAll" class="btn btn-small">Expand All</button>
              <button @click="collapseAll" class="btn btn-small">Collapse All</button>
            </template>
            <label class="auto-scroll">
              <input type="checkbox" v-model="autoScroll" />
              Auto-scroll
            </label>
            <!-- Show connect/disconnect only for non-finished builds -->
            <template v-if="!isBuildFinished() || effectiveState() === 'disconnected'">
              <button v-if="!connected" @click="emit('connect')" class="btn">
                Connect
              </button>
              <button v-else @click="emit('disconnect')" class="btn">
                Disconnect
              </button>
            </template>
            <!-- For finished builds, show "View Logs" if not connected and no logs loaded -->
            <button
              v-if="isBuildFinished() && !connected && logs.length === 0"
              @click="emit('connect')"
              class="btn btn-primary"
            >
              View Logs
            </button>
            <button @click="emit('clear')" class="btn btn-clear">
              Clear
            </button>
            <button @click="toggleFullscreen" class="btn btn-fullscreen" :title="fullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen'">
              <span v-if="fullscreen">&#x2716;</span>
              <span v-else>&#x26F6;</span>
            </button>
          </div>
        </div>

        <!-- Stage progress bar -->
        <div v-if="showStages && stageGroup.stages.length > 0" class="stage-progress-bar">
          <div
            v-for="(stage, index) in stageGroup.stages"
            :key="`progress-${index}`"
            class="progress-segment"
            :class="getStageStatusClass(stage.status)"
            :style="{ width: `${100 / stageGroup.stages.length}%` }"
            :title="`${stage.name}${stage.duration ? ` (${stage.duration}s)` : ''}`"
            @click="scrollToStage(stage.name)"
          >
            <span class="segment-label">{{ stage.name }}<span v-if="stage.duration" class="segment-duration"> {{ stage.duration }}s</span></span>
          </div>
        </div>

        <div
          ref="logContainer"
          class="log-content"
          @scroll="handleScroll"
          @copy="handleCopy"
        >
          <div v-if="logs.length === 0" class="empty">
            <template v-if="effectiveState() === 'connecting'">Connecting to log stream...</template>
            <template v-else-if="effectiveState() === 'replaying_history'">Loading log history...</template>
            <template v-else-if="connected">Waiting for logs...</template>
            <template v-else-if="isBuildFinished()">Click "View Logs" to load build logs.</template>
            <template v-else>No logs yet. Connect to start streaming.</template>
          </div>

          <!-- Virtual scrolling container -->
          <div
            v-else
            class="virtual-list"
            :style="{ height: `${virtualizer.getTotalSize()}px` }"
          >
            <template
              v-for="virtualRow in virtualizer.getVirtualItems()"
              :key="virtualRow.key"
            >
              <!-- Pre-stage log -->
              <div
                v-if="virtualItems[virtualRow.index].type === 'pre-stage-log'"
                class="log-line virtual-row"
                :class="{
                  stderr: getItemLog(virtualRow.index).stream === 'stderr',
                  selected: selectedLine === (getItemOriginalIndex(virtualRow.index) ?? -1) + 1
                }"
                :style="{
                  transform: `translateY(${virtualRow.start}px)`,
                  height: `${virtualRow.size}px`,
                }"
                @click="handleLineClick(virtualRow.index)"
              >
                <span class="line-number">{{ (getItemOriginalIndex(virtualRow.index) ?? 0) + 1 }}</span>
                <span class="time">{{ formatTime(getItemLog(virtualRow.index).timestamp) }}</span>
                <span class="stream">[{{ getItemLog(virtualRow.index).stream }}]</span>
                <span class="text" v-html="parseAnsi(getItemLog(virtualRow.index).line)"></span>
              </div>

              <!-- Stage header -->
              <div
                v-else-if="virtualItems[virtualRow.index].type === 'stage-header'"
                class="stage-header virtual-row"
                :class="getStageStatusClass(getItemStage(virtualRow.index).status)"
                :style="{
                  transform: `translateY(${virtualRow.start}px)`,
                  height: `${virtualRow.size}px`,
                }"
                @click="toggleStage(getItemStage(virtualRow.index).name)"
              >
                <span class="stage-toggle">{{ isStageCollapsed(getItemStage(virtualRow.index).name) ? '\u25B6' : '\u25BC' }}</span>
                <span class="stage-icon" :class="getStageStatusClass(getItemStage(virtualRow.index).status)">{{ getStageStatusIcon(getItemStage(virtualRow.index)) }}</span>
                <span class="stage-name">{{ getItemStage(virtualRow.index).name }}</span>
                <span v-if="getItemStage(virtualRow.index).duration" class="stage-duration">{{ getItemStage(virtualRow.index).duration }}s</span>
                <span class="stage-log-count">{{ getItemStage(virtualRow.index).logs.length }} lines</span>
              </div>

              <!-- Stage log -->
              <div
                v-else-if="virtualItems[virtualRow.index].type === 'stage-log'"
                class="log-line stage-log virtual-row"
                :class="[
                  getStageStatusClass(getItemStage(virtualRow.index).status),
                  {
                    stderr: getItemLog(virtualRow.index).stream === 'stderr',
                    selected: selectedLine === (getItemOriginalIndex(virtualRow.index) ?? -1) + 1
                  }
                ]"
                :style="{
                  transform: `translateY(${virtualRow.start}px)`,
                  height: `${virtualRow.size}px`,
                }"
                @click="handleLineClick(virtualRow.index)"
              >
                <span class="line-number">{{ (getItemOriginalIndex(virtualRow.index) ?? 0) + 1 }}</span>
                <span class="time">{{ formatTime(getItemLog(virtualRow.index).timestamp) }}</span>
                <span class="stream">[{{ getItemLog(virtualRow.index).stream }}]</span>
                <span class="text" v-html="parseAnsi(getItemLog(virtualRow.index).line)"></span>
              </div>

              <!-- Flat log -->
              <div
                v-else-if="virtualItems[virtualRow.index].type === 'flat-log'"
                class="log-line virtual-row"
                :class="{
                  stderr: getItemLog(virtualRow.index).stream === 'stderr',
                  selected: selectedLine === (getItemOriginalIndex(virtualRow.index) ?? -1) + 1
                }"
                :style="{
                  transform: `translateY(${virtualRow.start}px)`,
                  height: `${virtualRow.size}px`,
                }"
                @click="handleLineClick(virtualRow.index)"
              >
                <span class="line-number">{{ (getItemOriginalIndex(virtualRow.index) ?? 0) + 1 }}</span>
                <span class="time">{{ formatTime(getItemLog(virtualRow.index).timestamp) }}</span>
                <span class="stream">[{{ getItemLog(virtualRow.index).stream }}]</span>
                <span class="text" v-html="parseAnsi(getItemLog(virtualRow.index).line)"></span>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.log-viewer {
  display: flex;
  flex-direction: column;
  height: 400px;
  background: #1e1e1e;
  border-radius: 8px;
  overflow: hidden;
}

.log-viewer.fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 1000;
  background: transparent;
  border-radius: 0;
}

.fullscreen-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
}

.log-viewer-inner {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: #1e1e1e;
  border-radius: 8px;
  overflow: hidden;
}

.log-viewer-inner.fullscreen {
  position: absolute;
  top: 24px;
  left: 24px;
  right: 24px;
  bottom: 24px;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}

.log-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #2d2d2d;
  border-bottom: 1px solid #3d3d3d;
  flex-shrink: 0;
}

.toolbar-left,
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: #ccc;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.dot.pulse {
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.connected .dot {
  background: #22c55e;
}

.disconnected .dot {
  background: #ef4444;
}

.connecting .dot {
  background: #f59e0b;
}

.replaying .dot {
  background: #3b82f6;
}

.streaming .dot {
  background: #22c55e;
}

.finished .dot {
  background: #6b7280;
}

.log-count,
.stage-count {
  font-size: 0.85rem;
  color: #888;
}

.stage-count {
  padding-left: 8px;
  border-left: 1px solid #444;
}

/* Stage progress bar */
.stage-progress-bar {
  display: flex;
  height: 24px;
  background: #252525;
  border-bottom: 1px solid #3d3d3d;
  flex-shrink: 0;
}

.progress-segment {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  border-right: 1px solid #1e1e1e;
  cursor: pointer;
  transition: filter 0.2s;
  overflow: hidden;
}

.progress-segment:last-child {
  border-right: none;
}

.progress-segment:hover {
  filter: brightness(1.2);
}

.progress-segment.stage-status-pending {
  background: #3d3d3d;
}

.progress-segment.stage-status-running {
  background: #1d4ed8;
  animation: progress-pulse 1.5s ease-in-out infinite;
}

.progress-segment.stage-status-success {
  background: #166534;
}

.progress-segment.stage-status-failed {
  background: #991b1b;
}

@keyframes progress-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.segment-label {
  font-size: 0.7rem;
  color: #e5e5e5;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  padding: 0 4px;
  opacity: 0.9;
}

.segment-duration {
  opacity: 0.7;
  font-size: 0.65rem;
  margin-left: 4px;
}

.auto-scroll {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: #ccc;
  cursor: pointer;
}

.btn {
  padding: 4px 10px;
  font-size: 0.85rem;
  background: #3d3d3d;
  color: #ccc;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn:hover {
  background: #4d4d4d;
}

.btn-small {
  padding: 2px 8px;
  font-size: 0.75rem;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover {
  background: #2563eb;
}

.btn-fullscreen {
  font-size: 1rem;
  padding: 4px 8px;
}

.log-content {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  font-family: 'SF Mono', 'Menlo', 'Monaco', monospace;
  font-size: 0.85rem;
  line-height: 1.6;
}

.empty {
  color: #666;
  text-align: center;
  padding: 40px;
}

/* Virtual list container */
.virtual-list {
  position: relative;
  width: 100%;
}

.virtual-row {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  align-items: center;
}

/* Stage header styles */
.stage-header {
  gap: 8px;
  padding: 8px 12px;
  background: #2d2d2d;
  cursor: pointer;
  user-select: none;
  transition: background 0.2s;
  border-radius: 4px 4px 0 0;
  border-top: 2px solid #888;
  border-right: 2px solid #888;
  border-bottom: none;
  border-left: 3px solid #888;
  box-sizing: border-box;
}

.stage-header:hover {
  background: #363636;
}

.stage-header.stage-status-running {
  border-left-color: #3b82f6;
}

.stage-header.stage-status-success {
  border-left-color: #22c55e;
}

.stage-header.stage-status-failed {
  border-left-color: #ef4444;
}

.stage-toggle {
  color: #888;
  font-size: 0.75rem;
  width: 12px;
}

.stage-icon {
  font-size: 0.9rem;
  width: 16px;
  text-align: center;
}

.stage-icon.stage-status-pending {
  color: #888;
}

.stage-icon.stage-status-running {
  color: #3b82f6;
  animation: pulse 1.5s ease-in-out infinite;
}

.stage-icon.stage-status-success {
  color: #22c55e;
}

.stage-icon.stage-status-failed {
  color: #ef4444;
}

.stage-name {
  font-weight: 600;
  color: #e5e5e5;
  flex: 1;
}

.stage-duration {
  font-size: 0.8rem;
  color: #888;
  padding: 2px 6px;
  background: #3d3d3d;
  border-radius: 3px;
}

.stage-log-count {
  font-size: 0.75rem;
  color: #666;
}

.stage-log {
  padding-left: 24px;
  background: #252525;
  border-left: 3px solid #555;
  border-right: 1px solid #555;
}

.stage-log.stage-status-running {
  border-left-color: #3b82f6;
}

.stage-log.stage-status-success {
  border-left-color: #22c55e;
}

.stage-log.stage-status-failed {
  border-left-color: #ef4444;
}

.log-line {
  gap: 8px;
  color: #d4d4d4;
  padding: 0 4px;
  cursor: pointer;
}

.log-line .text {
  cursor: text;
}

.log-line:hover {
  background: rgba(255, 255, 255, 0.05);
}

.log-line.selected {
  background: rgba(59, 130, 246, 0.2);
  outline: 1px solid rgba(59, 130, 246, 0.5);
}

.log-line.stderr {
  color: #f87171;
}

.line-number {
  color: #555;
  flex-shrink: 0;
  width: 50px;
  text-align: right;
  user-select: none;
}

.log-line.selected .line-number {
  color: #3b82f6;
}

.time {
  color: #666;
  flex-shrink: 0;
  user-select: none;
}

.stream {
  color: #888;
  flex-shrink: 0;
  width: 60px;
  user-select: none;
}

.stderr .stream {
  color: #f87171;
}

.text {
  white-space: pre;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ANSI text styles */
.text :deep(.ansi-bold) { font-weight: bold; }
.text :deep(.ansi-dim) { opacity: 0.7; }
.text :deep(.ansi-italic) { font-style: italic; }
.text :deep(.ansi-underline) { text-decoration: underline; }

/* ANSI foreground colors */
.text :deep(.ansi-black) { color: #1e1e1e; }
.text :deep(.ansi-red) { color: #f87171; }
.text :deep(.ansi-green) { color: #4ade80; }
.text :deep(.ansi-yellow) { color: #facc15; }
.text :deep(.ansi-blue) { color: #60a5fa; }
.text :deep(.ansi-magenta) { color: #c084fc; }
.text :deep(.ansi-cyan) { color: #22d3ee; }
.text :deep(.ansi-white) { color: #e5e5e5; }

/* ANSI bright foreground colors */
.text :deep(.ansi-bright-black) { color: #6b7280; }
.text :deep(.ansi-bright-red) { color: #fca5a5; }
.text :deep(.ansi-bright-green) { color: #86efac; }
.text :deep(.ansi-bright-yellow) { color: #fde047; }
.text :deep(.ansi-bright-blue) { color: #93c5fd; }
.text :deep(.ansi-bright-magenta) { color: #d8b4fe; }
.text :deep(.ansi-bright-cyan) { color: #67e8f9; }
.text :deep(.ansi-bright-white) { color: #ffffff; }

/* ANSI background colors */
.text :deep(.ansi-bg-black) { background-color: #1e1e1e; }
.text :deep(.ansi-bg-red) { background-color: #dc2626; }
.text :deep(.ansi-bg-green) { background-color: #16a34a; }
.text :deep(.ansi-bg-yellow) { background-color: #ca8a04; }
.text :deep(.ansi-bg-blue) { background-color: #2563eb; }
.text :deep(.ansi-bg-magenta) { background-color: #9333ea; }
.text :deep(.ansi-bg-cyan) { background-color: #0891b2; }
.text :deep(.ansi-bg-white) { background-color: #e5e5e5; }

/* ANSI bright background colors */
.text :deep(.ansi-bg-bright-black) { background-color: #4b5563; }
.text :deep(.ansi-bg-bright-red) { background-color: #ef4444; }
.text :deep(.ansi-bg-bright-green) { background-color: #22c55e; }
.text :deep(.ansi-bg-bright-yellow) { background-color: #eab308; }
.text :deep(.ansi-bg-bright-blue) { background-color: #3b82f6; }
.text :deep(.ansi-bg-bright-magenta) { background-color: #a855f7; }
.text :deep(.ansi-bg-bright-cyan) { background-color: #06b6d4; }
.text :deep(.ansi-bg-bright-white) { background-color: #ffffff; }

/* Tablet breakpoint (768px) */
@media (max-width: 768px) {
  .log-viewer {
    min-height: 350px;
    height: auto;
  }

  .log-toolbar {
    flex-wrap: wrap;
    gap: 8px;
    padding: 8px;
  }

  .toolbar-left,
  .toolbar-right {
    flex-wrap: wrap;
    gap: 8px;
  }

  .btn {
    min-height: 36px;
  }
}

/* Phone breakpoint (480px) */
@media (max-width: 480px) {
  .log-viewer {
    min-height: 300px;
    height: auto;
    border-radius: 0;
    margin-left: calc(-1 * var(--mobile-padding, 12px));
    margin-right: calc(-1 * var(--mobile-padding, 12px));
    width: calc(100% + 2 * var(--mobile-padding, 12px));
  }

  .log-viewer.fullscreen {
    width: 100vw;
    margin-left: 0;
    margin-right: 0;
  }

  .log-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .toolbar-left {
    justify-content: space-between;
  }

  .toolbar-right {
    justify-content: flex-end;
  }

  /* Hide less-critical controls on phone */
  .auto-scroll,
  .btn-small {
    display: none;
  }

  .stage-count {
    display: none;
  }

  /* Touch-friendly progress bar on phone */
  .stage-progress-bar {
    height: 44px;
  }

  .progress-segment {
    flex-direction: column;
    gap: 2px;
    padding: 4px 2px;
  }

  .segment-label {
    font-size: 0.65rem;
    line-height: 1.2;
  }

  .segment-duration {
    display: block;
    font-size: 0.6rem;
    margin-left: 0;
  }

  .btn-clear {
    display: none;
  }

  .log-viewer-inner.fullscreen {
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 0;
  }

  .log-content {
    font-size: 0.75rem;
    padding: 8px;
  }

  .log-line {
    gap: 4px;
  }

  /* Hide line numbers, timestamps, and stream on mobile to save space */
  .line-number,
  .time,
  .stream {
    display: none;
  }

  .stage-header {
    padding: 6px 8px;
    font-size: 0.85rem;
  }

  .stage-duration,
  .stage-log-count {
    font-size: 0.7rem;
  }
}
</style>
